import { useTranslation } from "../../context/TranslationContext";
import Badge from "../ds/Badge";
import Button from "../ds/Button";
import Icon from "../ds/Icon";
import Modal from "../ds/Modal";
import UserProfileLink from "../UserProfileLink";
import { APP_FILLED, FilterChip, PLATFORMS, PlatformLogo, PlatformTile, formatWhen, shortLink } from "./shared";

export const STATUS_TONES = { pending: "warning", approved: "success", rejected: "danger" };

const TRACKING_TONES = {
  fresh: "neutral",
  active: "success",
  stable: "warning",
  dead: "neutral",
  completed: "neutral",
  bot_rejected: "warning",
};

const TRACKING_KEYS = {
  fresh: "mintSpace.tracking_fresh",
  active: "mintSpace.tracking_active",
  stable: "mintSpace.tracking_stable",
  dead: "mintSpace.tracking_dead",
  completed: "mintSpace.tracking_completed",
  bot_rejected: "mintSpace.tracking_botRejected",
};

const TrackingBadge = ({ status }) => {
  const { t } = useTranslation();
  if (!TRACKING_KEYS[status]) return null;
  return (
    <Badge size="s" tone={TRACKING_TONES[status]}>
      {t(TRACKING_KEYS[status])}
    </Badge>
  );
};

/** Why the engagement check turned a video down. */
const BotRejection = ({ submission }) => {
  const { t } = useTranslation();
  const reasons = submission.botDetectionSnapshot?.reasons || [];
  return (
    <div className="mt-space-4 text-footnote text-status-warning">
      <p>{t("mintSpace.rejectedBotDetection", { score: submission.botDetectionSnapshot?.score ?? "—" })}</p>
      {reasons.length > 0 && (
        <ul className="list-disc pl-space-16 text-label-secondary">
          {reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** The appeal's state on a rejected row, or the way to start one on your own. */
const AppealState = ({ submission, canAppeal, onAppeal }) => {
  const { t } = useTranslation();
  if (submission.status !== "rejected") return null;
  if (submission.appealResolution === "denied") return <Badge tone="danger">{t("mintSpace.appealDenied")}</Badge>;
  if (submission.appealMessage) {
    return submission.appealResolution ? null : <Badge tone="warning">{t("mintSpace.appealUnderReview")}</Badge>;
  }
  if (!canAppeal) return null;
  return (
    <Button size="s" variant="gray" onClick={() => onAppeal(submission)}>
      {t("mintSpace.appeal")}
    </Button>
  );
};

/** Dry run and Check now re-read a video's views; they stay plain test tools. */
const CheckTools = ({ submission, actions }) => {
  const checking = actions.busy === `${submission._id}check`;
  return (
    <>
      <Button
        size="s"
        variant="gray"
        disabled={Boolean(actions.busy)}
        title="Dry run — scrape & parse but don't save"
        onClick={() => actions.checkNow(submission._id, true)}
      >
        {checking ? "…" : "Dry run"}
      </Button>
      <Button
        size="s"
        variant="tinted"
        disabled={Boolean(actions.busy)}
        title="Scrape, save results, and reschedule next check"
        onClick={() => actions.checkNow(submission._id, false)}
      >
        {checking ? "…" : "Check now"}
      </Button>
    </>
  );
};

const metaLine = (t, submission, showCampaign) => {
  const parts = [formatWhen(t, submission.createdAt)];
  if (showCampaign && submission.campaign?.campaignName) parts.unshift(submission.campaign.campaignName);
  if (submission.status === "approved" && submission.viewCount > 0) {
    parts.push(t("campaignFlow.viewsCount", { count: submission.viewCount.toLocaleString() }));
  }
  if (submission.status === "pending") parts.push(t("campaignFlow.viewsCounting"));
  if (submission.status === "rejected" && submission.notes) parts.push(submission.notes);
  return parts.filter(Boolean).join(" · ");
};

/** Figma V4 "Submission": a creator's own video, what it earned, and where it stands. */
export const MySubmissionRow = ({ submission, showCampaign = false, tools = false, actions }) => {
  const { t } = useTranslation();
  const earned = submission.status === "approved" && submission.totalEarned > 0 ? `${submission.totalEarned.toFixed(2)} ₾` : "—";
  const botRejected = submission.status === "rejected" && submission.rejectionSource === "bot_detection";

  return (
    <li className="flex flex-col gap-space-12 px-space-16 py-space-16 min-[600px]:flex-row min-[600px]:items-center min-[600px]:px-space-20">
      <div className="flex min-w-0 flex-1 items-center gap-space-12">
        <PlatformTile platform={submission.platform} />
        <div className="flex min-w-0 flex-1 flex-col">
          <a
            href={submission.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-subheadline-semibold text-label-primary hover:underline"
          >
            {shortLink(submission.videoLink)}
          </a>
          <p className="truncate text-footnote text-label-secondary">{metaLine(t, submission, showCampaign)}</p>
          {botRejected && <BotRejection submission={submission} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-space-12 pl-[48px] min-[600px]:justify-end min-[600px]:pl-0">
        <p className="text-headline text-label-primary">{earned}</p>
        {submission.status === "approved" && <TrackingBadge status={submission.trackingStatus} />}
        <Badge tone={STATUS_TONES[submission.status]}>{t(`campaignFlow.status_${submission.status}`)}</Badge>
        <AppealState submission={submission} canAppeal onAppeal={actions.openAppeal} />
        {tools && submission.status === "approved" && <CheckTools submission={submission} actions={actions} />}
      </div>
    </li>
  );
};

/** Figma O2 "Row": who sent it, the video, and the decision. */
export const ReviewSubmissionRow = ({ submission, canReview = true, showCampaign = false, ownId, actions }) => {
  const { t } = useTranslation();
  const creator = submission.submittedBy;
  const username = creator?.username || creator?.email || t("common.unknown");
  const own = String(creator?._id || "") === String(ownId || "");
  const botRejected = submission.status === "rejected" && submission.rejectionSource === "bot_detection";

  return (
    <li className="flex flex-col gap-space-12 px-space-16 py-space-16 min-[800px]:flex-row min-[800px]:items-center min-[800px]:px-space-20">
      <div className="flex min-w-0 items-center gap-space-12 min-[800px]:w-[200px] min-[800px]:shrink-0">
        <UserProfileLink user={creator} avatarSize="md" showAvatar nameClassName="sr-only" fallback={username} />
        <div className="flex min-w-0 flex-col">
          <UserProfileLink
            user={creator}
            showAvatar={false}
            nameClassName="truncate text-subheadline-semibold text-label-primary"
            fallback={username}
          />
          {creator?.username && <p className="truncate text-caption-1 text-label-tertiary">@{creator.username}</p>}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 items-center gap-space-8">
        <PlatformLogo platform={submission.platform} size={20} />
        <div className="flex min-w-0 flex-1 flex-col">
          <a
            href={submission.videoLink}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-subheadline text-label-primary hover:underline"
          >
            {t("mintSpace.viewContent")}
          </a>
          <p className="truncate text-footnote text-label-secondary">{metaLine(t, submission, showCampaign)}</p>
          {botRejected && <BotRejection submission={submission} />}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-space-8 min-[800px]:justify-end">
        {submission.status === "pending" && canReview ? (
          <>
            <Button
              size="s"
              variant="gray"
              disabled={Boolean(actions.busy)}
              loading={actions.busy === `${submission._id}rejected`}
              onClick={() => actions.review(submission._id, "rejected")}
            >
              {t("mintSpace.reject")}
            </Button>
            <Button
              size="s"
              className={actions.busy ? "" : APP_FILLED}
              disabled={Boolean(actions.busy)}
              loading={actions.busy === `${submission._id}approved`}
              icon={<Icon name="check" size={16} />}
              onClick={() => actions.review(submission._id, "approved")}
            >
              {t("mintSpace.approve")}
            </Button>
          </>
        ) : (
          <Badge tone={STATUS_TONES[submission.status]}>{t(`campaignFlow.status_${submission.status}`)}</Badge>
        )}
        {submission.status === "approved" && <TrackingBadge status={submission.trackingStatus} />}
        <AppealState submission={submission} canAppeal={own} onAppeal={actions.openAppeal} />
        {submission.status === "approved" && <CheckTools submission={submission} actions={actions} />}
      </div>
    </li>
  );
};

/** Status chips with counts, and platform chips, over a submission list. */
export const SubmissionFilters = ({ submissions, status, onStatus, platform, onPlatform, platforms = PLATFORMS }) => {
  const { t } = useTranslation();
  const count = (key) => (key === "all" ? submissions.length : submissions.filter((row) => row.status === key).length);
  const labels = {
    all: t("common.all"),
    pending: t("campaignFlow.status_pending"),
    approved: t("campaignFlow.status_approved"),
    rejected: t("campaignFlow.status_rejected"),
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-space-8">
      <div className="flex flex-wrap gap-space-8">
        {Object.keys(labels).map((key) => (
          <FilterChip key={key} selected={status === key} onClick={() => onStatus(key)}>
            {labels[key]} · {count(key)}
          </FilterChip>
        ))}
      </div>
      <div className="flex flex-wrap gap-[6px]">
        <FilterChip selected={platform === "all"} onClick={() => onPlatform("all")}>
          {t("campaignFlow.allPlatforms")}
        </FilterChip>
        {platforms.map((entry) => (
          <FilterChip key={entry.id} selected={platform === entry.id} onClick={() => onPlatform(entry.id)}>
            <PlatformLogo platform={entry} size={16} />
            {entry.name}
          </FilterChip>
        ))}
      </div>
    </div>
  );
};

const Result = ({ title, children, wide = false }) => (
  <div className={`flex flex-col gap-space-4 rounded-[22px] bg-bg-inset p-space-16 text-footnote ${wide ? "min-[600px]:col-span-2" : ""}`}>
    <p className="text-caps-label uppercase text-label-tertiary">{title}</p>
    {children}
  </div>
);

/** The popups every submission list shares: writing an appeal, and a check's result. */
export const SubmissionDialogs = ({ actions, isDark }) => {
  const { t } = useTranslation();
  const result = actions.checkResult;

  return (
    <>
      <Modal
        open={Boolean(actions.appealTarget)}
        onClose={actions.closeAppeal}
        title={t("mintSpace.appeal")}
        description={t("mintSpace.appealMessage")}
        closeLabel={t("common.close")}
        isDark={isDark}
      >
        <div className="flex flex-col gap-space-8">
          <textarea
            value={actions.appealText}
            onChange={(event) => actions.setAppealText(event.target.value.slice(0, 1000))}
            rows={5}
            placeholder={t("mintSpace.appealPlaceholder")}
            className="w-full resize-none rounded-[22px] bg-fill-tertiary px-space-16 py-space-12 text-callout text-label-primary outline-none placeholder:text-label-tertiary focus:shadow-[0_0_0_2px_var(--color-focus-ring)]"
          />
          <p className="text-right text-caption-1 text-label-tertiary">{actions.appealText.length}/1000</p>
        </div>
        <div className="flex justify-end gap-space-8">
          <Button variant="gray" size="m" disabled={actions.appealing} onClick={actions.closeAppeal}>
            {t("common.cancel")}
          </Button>
          <Button size="m" loading={actions.appealing} disabled={!actions.appealText.trim()} onClick={actions.submitAppeal}>
            {t("mintSpace.submitAppeal")}
          </Button>
        </div>
      </Modal>

      <Modal
        open={Boolean(result)}
        onClose={actions.closeCheckResult}
        title={result?.saved === false ? "Check result (dry run — not saved)" : "Check result"}
        closeLabel={t("common.close")}
        width="min-[600px]:w-[640px]"
        isDark={isDark}
      >
        {result?.error ? (
          <p className="font-mono text-subheadline text-status-danger">{result.error}</p>
        ) : (
          <div className="grid grid-cols-1 gap-space-12 font-mono min-[600px]:grid-cols-2">
            <Result title="Views">
              <p>Before: {result?.previousViews?.toLocaleString() ?? 0}</p>
              <p>After: {result?.currentViews?.toLocaleString() ?? 0}</p>
              <p className="text-label-secondary">+{((result?.currentViews || 0) - (result?.previousViews || 0)).toLocaleString()}</p>
            </Result>
            <Result title="Earnings">
              <p>Rate: {result?.earnings?.rateType ?? "—"}</p>
              <p>New: +{(result?.earnings?.newEarnings ?? 0).toFixed(2)}</p>
              <p>Total: {(result?.earnings?.totalEarnings ?? 0).toFixed(2)}</p>
            </Result>
            <Result title="Next check">
              <p>Status: {result?.nextCheck?.status ?? "—"}</p>
              <p>In: {result?.nextCheck?.intervalHours ?? "—"}h</p>
              <p className="text-label-secondary">
                {result?.nextCheck?.nextCheckAt ? new Date(result.nextCheck.nextCheckAt).toLocaleTimeString() : "tracking complete"}
              </p>
            </Result>
            <Result title="Algorithm">
              <p>Age: {result?.algorithm?.submissionAgeHours ?? "—"}h</p>
              <p>Growth (last check): {result?.algorithm?.growthSinceLastCheck?.toLocaleString() ?? "—"}</p>
              <p>Growth (24h): {result?.algorithm?.growthLast24h?.toLocaleString() ?? "—"}</p>
            </Result>
            {result?.parseDetails && Object.keys(result.parseDetails).length > 0 && (
              <Result title="Parse details" wide>
                {Object.entries(result.parseDetails).map(([key, value]) => (
                  <p key={key}>
                    {key}: <span className="text-label-secondary">{String(value)}</span>
                  </p>
                ))}
              </Result>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
